import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";

import { useQuery } from "@tanstack/react-query";

import { getImageUrl } from "simplydo/core/images";
import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { css, cx, styled } from "simplydo/react/styled";
import { mobileOnly, tabletUp } from "simplydo/react/theme";
import { Divider } from "simplydo/react/ui/divider";
import {
  Dropdown,
  DropdownDivider,
  DropdownHeader,
  DropdownItem,
  DropdownMenu,
} from "simplydo/react/ui/dropdown";
import { H4 } from "simplydo/react/ui/header";
import { Image } from "simplydo/react/ui/image";
import { Input } from "simplydo/react/ui/input";
import { Menu, MenuItem } from "simplydo/react/ui/menu";
import { Popup } from "simplydo/react/ui/popup";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import { useUserReload } from "utils/useUserReload";
import util from "utils/utils";

import ProductTour from "components/lib/ProductTour";

import CircularButtonWithNumber from "./CircularButtonWithNumber";

const styledDropdownItemClass = css`
  &.is-clickable {
    cursor: pointer;

    &:hover {
      background-color: #e9ebee;
    }
  }

  &:focus {
    outline: 2px solid #007bff;
  }

  img {
    height: 20px;
    width: 30px !important;
    object-fit: contain;
  }
`;

const StyledNavBarSubSegment = styled.div`
  background-color: #f4f4f4;
  border-left: 1px solid #dedede;
  padding: 0 16px 0 14px;
  height: 49px;
  margin-right: -10px;
  display: flex;
  justify-content: center;
  align-items: center;

  ${mobileOnly} {
    margin-left: 10px;
  }

  ${tabletUp} {
    margin-left: 20px;
  }
`;

const OrgListItem = ({
  organisation,
  onClick,
  showCode,
  isRecent,
}: {
  organisation: OpenAPI.Schemas["Organisation"];
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  showCode?: boolean;
  isRecent?: boolean;
}) => (
  <DropdownItem
    className={cx(styledDropdownItemClass, onClick && "is-clickable")}
    onClick={onClick}
  >
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        gap: 8,
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: 8,
          alignItems: "center",
        }}
      >
        {isRecent ? null : (
          <Image
            src={util.getImageUrl(
              organisation,
              "darkLogo",
              ["128x128", "256x256"],
              "images/icon.png",
            )}
            alt={organisation.name}
            width={30}
            height={30}
          />
        )}
        {organisation.name}
      </div>
      <span style={{ color: "grey" }}>
        {showCode ? organisation.code : null}
      </span>
    </div>
  </DropdownItem>
);

type OrgSwitcherDropdownProps = {
  style?: React.CSSProperties;
  basic?: boolean;
  trigger?: React.ReactNode;
};

export const OrgSwitcherDropdown = ({
  style,
  basic,
  trigger,
}: OrgSwitcherDropdownProps) => {
  const user = useAppSelector((state) => state.user);

  const reloadUser = useUserReload();
  const [orgSearch, setOrgSearch] = useState("");
  const [recentOrgs, setRecentOrgs] = useState<
    OpenAPI.Schemas["Organisation"][]
  >([]);
  const [superadminOrgSelectorVisible, setSuperadminOrgSelectorVisible] =
    useState(true);

  const orgsQuery = useQuery(
    initUseQuery(api.organisations.getAll, [{ page: 1, query: orgSearch }]),
  );

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (basic) return;
    const recentOrgsLocalStorage = util.localStorageIsSupported()
      ? JSON.parse(localStorage.getItem("recentOrgs") || "[]")
      : [];
    setRecentOrgs(recentOrgsLocalStorage.slice(0, 5));
  }, [basic]);

  const switchOrganisation = useCallback(
    (orgId: string, addToRecents = true) => {
      if (!user?._id) return;
      api.users.switchOrganisation(
        user._id,
        orgId,
        (data) => {
          if (util.localStorageIsSupported() && addToRecents) {
            // Recently switched organisations are stored in local storage, to a maximum of 5
            // Only add the organisation to the list if it's not already in the list
            const newRecentOrgs = recentOrgs.filter((org) => org._id !== orgId);
            newRecentOrgs.unshift(data.organisation);
            setRecentOrgs(newRecentOrgs.slice(0, 5));
            localStorage.setItem(
              "recentOrgs",
              JSON.stringify(newRecentOrgs.slice(0, 5)),
            );
          }

          dispatch(actions.user.switchOrganisation(data.organisation));
          reloadUser();
        },
        (err) => {
          toast.error(err.message);
        },
      );
    },
    [dispatch, recentOrgs, user, reloadUser],
  );

  if (!user?.ownerOrganisation) {
    return null;
  }

  const orgs = orgsQuery.data?.organisations ?? [];
  const ownerOrganisation = user.ownerOrganisation;
  const ownerOrganisations = user.ownerOrganisations ?? [];

  return (
    <Dropdown
      trigger={
        <div tabIndex={0} role="button" style={style}>
          {trigger ?? (
            <CircularButtonWithNumber
              image={getImageUrl(
                ownerOrganisation,
                "favicon",
                ["128x128", "256x256"],
                "images/icon.png",
              )}
              count={ownerOrganisations.length || undefined}
              style={style}
              labelProps={{ color: "grey" }}
              className="org-switcher-tour-step-1"
              square
            />
          )}
        </div>
      }
    >
      <DropdownMenu style={{ margin: "5px 0 0 0" }}>
        <DropdownHeader>Your organisations</DropdownHeader>
        <OrgListItem organisation={ownerOrganisation} showCode />
        {ownerOrganisations.length > 1 ||
        (ownerOrganisations.length === 1 &&
          ownerOrganisations[0]?._id !== ownerOrganisation._id) ? (
          <>
            {ownerOrganisations
              .filter(
                (organisation) => organisation._id !== ownerOrganisation._id,
              )
              .map((organisation) => (
                <OrgListItem
                  showCode
                  key={organisation._id}
                  organisation={organisation}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    switchOrganisation(organisation._id);
                  }}
                />
              ))}
          </>
        ) : null}
        {util.hasPermission(user, "super.switchOrg", ownerOrganisation._id) &&
        superadminOrgSelectorVisible &&
        (recentOrgs.length > 0 || orgs.length > 0 || !!orgSearch) &&
        !basic ? (
          <>
            <DropdownDivider />
            {recentOrgs.length > 0 ? (
              <>
                <DropdownHeader>Recent organisations</DropdownHeader>
                {recentOrgs.map((organisation) => (
                  <OrgListItem
                    key={organisation._id}
                    organisation={organisation}
                    isRecent
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      switchOrganisation(organisation._id, false);
                    }}
                  />
                ))}
                <DropdownDivider />
              </>
            ) : null}
            <DropdownHeader>Find an organisation</DropdownHeader>
            <div style={{ padding: "0.5rem 0.8rem" }}>
              <Input
                fluid
                size="small"
                value={orgSearch}
                onChange={(_e, { value }) => setOrgSearch(`${value ?? ""}`)}
                onKeyDownCapture={(e: React.KeyboardEvent) => {
                  // The dropdown component stops propagation if space if pressed - if a key down happens in this components we always want to stop propagation here at the search
                  if (e.key === " ") {
                    e.stopPropagation();
                  }
                }}
                placeholder="Search for org"
                onFocus={(e: React.FocusEvent) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                loading={orgsQuery.isLoading}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {orgs.map((organisation) => (
                <OrgListItem
                  key={organisation._id}
                  organisation={organisation}
                  showCode
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    switchOrganisation(organisation._id);
                  }}
                />
              ))}
            </div>
            <DropdownItem
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setSuperadminOrgSelectorVisible(false);
              }}
            >
              Hide superadmin orgs this session
            </DropdownItem>
          </>
        ) : null}
      </DropdownMenu>
    </Dropdown>
  );
};

const OrgSwitcherTray = ({ style }: { style?: React.CSSProperties }) => {
  const user = useAppSelector((state) => state.user);

  const showOrgSwitcherTray = useMemo(
    () =>
      (user &&
        (util.canAccessSuperadminOrgList(user) ||
          (user.ownerOrganisations?.length &&
            user.ownerOrganisations.length > 1))) ??
      false,
    [user],
  );

  if (!user) {
    return null;
  }
  if (!showOrgSwitcherTray) {
    return null;
  }
  return (
    <>
      <ProductTour
        tour="orgSwitcher"
        isReady={!!user?.ownerOrganisations?.length}
      />
      <StyledNavBarSubSegment>
        <OrgSwitcherDropdown style={style} />
      </StyledNavBarSubSegment>
    </>
  );
};

export default OrgSwitcherTray;
